import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useI18n } from "../../../hooks/useI18n";
import { AppState } from "../../../store";
import {
  StoreOrder,
  StoreOrderId,
  StoreOrderState
} from "../../../models/Store";
import { StoreOrdersState } from "../../../store/stores/types";
import { thunkFetchStoreOrders } from "../../../store/stores/actions";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectProps
} from "@material-ui/core";
import moment from "moment";

interface StoreOrderSelectProps extends Omit<SelectProps, "loading"> {
  storeId: string;
  label?: string;
  onSelected?: (value: StoreOrder) => void;
  variant?: "filled" | "outlined" | "standard";
  value?: StoreOrder["id"];
  error?: boolean;
}

export const StoreOrderSelect: React.FunctionComponent<
  StoreOrderSelectProps
> = ({
  storeId,
  label = "By distributor",
  onSelected,
  onChange,
  variant = "standard",
  value,
  error = false,
  ...props
}) => {
  const dispatch = useDispatch();
  const storeOrdersState = useSelector<AppState, StoreOrdersState>(
    s => s.storeOrders
  );
  const { orders } = storeOrdersState;
  const [state, setState] = useState<ReadonlyArray<StoreOrder>>([]);
  const { t } = useI18n();

  const handleChange = (
    event: React.ChangeEvent<{ name?: string; value: unknown }>,
    child: React.ReactNode
  ) => {
    if (onSelected) {
      let store = state.find(s => s.id === event.target.value);
      if (store) {
        onSelected(store);
      }
    }
    if (onChange) {
      onChange(event, child);
    }
  };

  useEffect(() => {
    let filtered = orders && orders.length > 0 && orders;

    if (filtered) {
      setState(filtered);
    }
  }, [orders]);

  useEffect(() => {
    // always load
    setState([]);
    dispatch(thunkFetchStoreOrders(storeId, [StoreOrderState.Ordered], true));
  }, [storeId]);

  return (
    <FormControl variant={variant} className={props.className} error={error}>
      <InputLabel
        id="store-order-select-label"
        style={
          variant === "outlined"
            ? {
                backgroundColor: "#fff",
                paddingLeft: "0.5rem",
                paddingRight: "0.5rem"
              }
            : undefined
        }
      >
        {t("common.formFields.byDistributor")}
      </InputLabel>
      <Select
        value={value}
        onChange={handleChange}
        labelId="store-order-select-label"
        id="store-order-select"
        autoWidth={true}
      >
        {state && state.length === 0 && (
          <MenuItem disabled key={0} value={0}>
            No orders found
          </MenuItem>
        )}

        {state &&
          state.length > 0 &&
          state.map(item => (
            <MenuItem key={item.id} value={item.id}>
              {item.distributors[0].name} |{" "}
              {moment(item.estimatedDelivery).format("YYYY-MM-DD")}
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  );
};
